import type { FinancialsWindow, Period } from "@/api/types"

export const ALLOWED_EMAIL_DOMAIN = "hecla-em.com"

export const GENERIC_ERROR_MESSAGE = "Something went wrong. Please try again."

export const REQUIRE_MFA = import.meta.env.VITE_REQUIRE_MFA === "true"

export const GAS_STORAGE_DEFAULT_MEAN_YEARS = 10

export const STALE_DATA_THRESHOLD_DAYS = 3

export const DEFAULT_STALE_TIME = 5 * 60 * 1000

export const SHORT_STALE_TIME = 60 * 1000

export const CURRENT_YEAR = new Date().getFullYear()

export const TIMEZONE_OPTIONS = [
	{ value: "none", label: "Not set" },
	{ value: "America/Vancouver", label: "Vancouver (PST/PDT)" },
	{ value: "Europe/London", label: "London (GMT/BST)" },
	{ value: "Europe/Oslo", label: "Oslo (CET/CEST)" },
	{ value: "Asia/Kuala_Lumpur", label: "Kuala Lumpur (MYT)" },
] as const

export const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 25] as const
export const DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE_OPTIONS[1]
export const DEFAULT_ROW_HEIGHT = 40

// Trading completed-trades card period menu
interface PeriodOption {
	label: string
	shortLabel: string
	comparison?: string
}

export const PERIOD_ORDER: Period[] = ["mtd", "qtr", "ytd", "all"]

export const PERIOD_OPTIONS: Record<Period, PeriodOption> = {
	mtd: {
		label: "Month to date",
		shortLabel: "MTD",
		comparison: "Compared with the same point last month",
	},
	qtr: {
		label: "Quarter to date",
		shortLabel: "QTD",
		comparison: "Compared with the same point last quarter",
	},
	ytd: {
		label: "Year to date",
		shortLabel: "YTD",
		comparison: "Compared with the same point last year",
	},
	all: { label: "All time", shortLabel: "All" },
}

// Financials page
export const WINDOW_OPTIONS: { label: string; value: FinancialsWindow }[] = [
	{ label: "12M", value: "12m" },
	{ label: "24M", value: "24m" },
	{ label: "YTD", value: "ytd" },
	{ label: "QTD", value: "qtd" },
]

export const DEFAULT_FINANCIALS_WINDOW: FinancialsWindow = "12m"

const WINDOW_VALUES = new Set<string>(WINDOW_OPTIONS.map((o) => o.value))

export function isFinancialsWindow(value: unknown): value is FinancialsWindow {
	return typeof value === "string" && WINDOW_VALUES.has(value)
}

export const WINDOW_LABELS: Record<FinancialsWindow, string> = {
	"12m": "trailing 12 months",
	"24m": "trailing 24 months",
	ytd: "year to date",
	qtd: "this quarter",
}

export const CURRENCIES = ["EUR", "GBP", "USD"] as const

export type FeeType = "eum" | "annual" | "quarterly"

export const FEE_OPTIONS: { value: FeeType; label: string }[] = [
	{ value: "eum", label: "EUM %" },
	{ value: "annual", label: "Annual Fee" },
	{ value: "quarterly", label: "Quarterly Fee" },
]

export const USAGE_DAYS = 90

export const USAGE_PERIODS = [7, 30, 90, 365] as const

export type UsagePeriod = (typeof USAGE_PERIODS)[number]

export const USAGE_PERIOD_OPTIONS: {
	label: string
	value: `${UsagePeriod}`
}[] = [
	{ label: "7D", value: "7" },
	{ label: "30D", value: "30" },
	{ label: "90D", value: "90" },
	{ label: "1Y", value: "365" },
]
